import { useState } from 'preact/hooks'

import { t } from '../i18n/t.js'
import type { SicherungEintrag } from './sicherung.js'
import {
  ARTEN, artName, erklaerung, feldName, platzhalter, pruefen, tastatur,
  type GeheimnisArt,
} from './geheimnisArt.js'
import {
  passkeysMoeglich, prfAussichtslos, prfAussichtslosMerken,
} from './passkeyEntsperren.js'

/**
 * Entsperrt den Schlüsselspeicher.
 *
 * ## Warum dieser Bildschirm überhaupt kommt
 *
 * Der Schlüssel für die lokalen Inhalte liegt nur im Arbeitsspeicher — beim
 * Neuladen, im neuen Tab, nach dem Kaltstart der App ist er weg. Die Sitzung
 * hält dagegen 30 Tage. Es gibt also viele Starts ohne Anmeldung, und genau
 * dort steht dieser Bildschirm.
 *
 * ## Der Passkey ist der Hauptweg, das Getippte die Rückfallebene
 *
 * Bis Phase 18 lieferte nur die Anmeldezeremonie ein PRF-Geheimnis; wer
 * angemeldet blieb, tippte seinen Satz praktisch täglich. Für „Kind bis Oma"
 * (Anf. 75) war das der Punkt, an dem die App unbenutzbar wird. Jetzt fragt
 * die App den Passkey auch ohne Anmeldung.
 *
 * Das getippte Geheimnis bleibt: für Geräte ohne PRF-Unterstützung, als
 * zweiter Weg, wenn der Passkey verloren geht — und alle vierzehn Tage als
 * Kontrolle, damit es niemand vergisst.
 *
 * ## Drei Zwecke, ein Bildschirm
 *
 *   `neu`         Erste Einrichtung: Art wählen, Geheimnis festlegen.
 *   `entsperren`  Der Alltag: Passkey, sonst tippen.
 *   `kontrolle`   Alle vierzehn Tage: tippen, der Passkey zählt hier nicht.
 *
 * ## Sichtbar tippen — Dans Vorgabe, und die Begründung dazu
 *
 * Beim Anlegen und bei der Kontrolle steht das Getippte **im Klartext** da,
 * nicht als Punkte. Das ist keine Nachlässigkeit, sondern der Punkt der Übung:
 * Beides sind Momente, in denen jemand sich etwas einprägen soll. Punkte
 * verhindern genau das — man tippt, sieht nichts, und merkt sich nichts. Wer
 * eine PIN nicht lesen kann, während er sie festlegt, hat sie in vierzehn
 * Tagen nicht im Kopf.
 *
 * Beim gewöhnlichen Entsperren gilt das Gegenteil: Das passiert im Bus, im
 * Wartezimmer, neben fremden Augen. Dort sind Punkte der Vorgabewert. Der
 * Schalter ist überall da, in beide Richtungen.
 *
 * **Eine Folge davon:** Solange das Eingetippte sichtbar ist, entfällt beim
 * Anlegen die Wiederholung. Man liest ja, was dasteht — ein zweites Feld wäre
 * nur eine Hürde ohne Nutzen. Wer auf Punkte umschaltet, bekommt es zurück.
 */

/** „Später" beim Angebot — einmal reicht, Nachfragen wäre Nörgeln. */
const NICHT_FRAGEN = 'fh-passkey-nicht-fragen'

function merker(name: string): boolean {
  try { return localStorage.getItem(name) === '1' } catch { return false }
}
function merken(name: string): void {
  try { localStorage.setItem(name, '1') } catch { /* Privatmodus: dann eben nicht */ }
}

export type Zweck = 'neu' | 'entsperren' | 'kontrolle'

export function Entsperren({
  zweck, art, passkeyWeg, mitGeheimnis, mitPasskey, passkeyNachruesten,
  onArt, onFertig, onVergessen, onAufschieben, mitNotfallblatt,
  sicherungen = [], wiederherstellen,
}: {
  /**
   * Der Rückweg (Phase 20): Sicherungen dieser Person auf dem Server. Nur bei
   * leerem Speicher gefüllt — dann steht vor dem „Womit schützen?" die Frage
   * „Du hattest schon ein Gerät?".
   */
  sicherungen?: SicherungEintrag[]
  /** Holt eine Sicherung auf dieses Gerät und öffnet sie mit den Wörtern. */
  wiederherstellen?: ((deviceId: string, woerter: string) => Promise<void>) | undefined
  /**
   * Der dritte Weg: die 12 Wörter vom Notfall-Blatt. Nur gesetzt, wenn eines
   * hinterlegt ist; sonst gibt es das Feld nicht.
   */
  mitNotfallblatt?: ((woerter: string) => Promise<void>) | undefined
  zweck: Zweck
  /** Satz, Passwort oder PIN — bestimmt Beschriftung, Tastatur und Mindestlänge. */
  art: GeheimnisArt
  /** Ist der Passkey für DIESEN Speicher schon als Weg eingerichtet? */
  passkeyWeg: boolean
  mitGeheimnis: (geheimnis: string) => Promise<void>
  /** `false` = Zeremonie lief, der Authenticator liefert aber kein PRF. */
  mitPasskey: () => Promise<boolean>
  passkeyNachruesten: () => Promise<boolean>
  /** Nur bei der ersten Einrichtung: die Art umstellen. */
  onArt: (art: GeheimnisArt) => void
  onFertig: () => void
  /**
   * Der Notausgang: Speicher auf diesem Gerät leeren und abmelden.
   *
   * Dieser Bildschirm riegelt die App ab — ohne ihn kommt jemand, der sein
   * Geheimnis vergessen hat, nicht einmal mehr an den Abmelden-Knopf. Das ist
   * beim ersten echten Gebrauch aufgefallen, nicht beim Lesen des Codes.
   */
  onVergessen: () => Promise<void>
  /**
   * Nur bei der Kontrolle: einmal per Passkey hinein, in drei Tagen wieder.
   * `false` = der Passkey hat nichts geliefert; dann bleibt nur das Tippen.
   */
  onAufschieben: () => Promise<boolean>
}) {
  const [schritt, setSchritt] = useState<'entsperren' | 'anbieten' | 'vergessen' | 'wiederherstellen'>('entsperren')
  // Die neueste Sicherung ist vorgewählt — bei einem Gerät ist es die einzige.
  const [gewaehlt, setGewaehlt] = useState<string | null>(sicherungen[0]?.deviceId ?? null)
  const [zurueckgeholt, setZurueckgeholt] = useState(false)
  const [geheimnis, setGeheimnis] = useState('')
  const [wiederholung, setWiederholung] = useState('')
  const [sichtbar, setSichtbar] = useState(zweck !== 'entsperren')
  const [busy, setBusy] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)
  const [hinweis, setHinweis] = useState<string | null>(null)
  const [woerter, setWoerter] = useState('')

  const neu = zweck === 'neu'

  async function mitBlatt() {
    if (!mitNotfallblatt) return
    setBusy(true)
    setFehler(null)
    try {
      await mitNotfallblatt(woerter)
      onFertig()
    } catch (e) {
      // `RecoveryFehler` (Tippfehler, Prüfsumme) und `FalscherWeg` (anderes
      // Blatt) bringen beide einen Satz mit, der dem Nutzer etwas sagt.
      setFehler(e instanceof Error && e.message ? e.message : t('Das stimmt nicht. Versuch es noch einmal.'))
    } finally {
      setBusy(false)
    }
  }
  async function holen() {
    if (!wiederherstellen || !gewaehlt) return
    setBusy(true)
    setFehler(null)
    try {
      await wiederherstellen(gewaehlt, woerter)
      setWoerter('')
      setZurueckgeholt(true)
      setHinweis(t('Deine Nachrichten sind zurück. Leg jetzt fest, womit du sie auf diesem Gerät schützt.'))
      setSchritt('entsperren')
    } catch (e) {
      setFehler(e instanceof Error && e.message ? e.message : t('Das stimmt nicht. Versuch es noch einmal.'))
    } finally {
      setBusy(false)
    }
  }

  const passkeyAngeboten = passkeysMoeglich() && !prfAussichtslos()
  // Bei der Kontrolle steht der Passkey-Knopf NICHT da. Ein Knopf, der die
  // Abfrage umgeht, macht sie zur Zierde — dann könnte man sie weglassen.
  const passkeyKnopf = passkeyAngeboten && passkeyWeg && zweck === 'entsperren'
  const wiederholungNoetig = neu && !sichtbar

  function fertigOderAnbieten() {
    // Nach dem Geheimnis: einmal anbieten, künftig den Finger zu nehmen. Das ist
    // der ganze Zweck der Übung — ein Schalter in den Einstellungen, den niemand
    // findet, hätte nichts geändert.
    if (passkeyAngeboten && !passkeyWeg && !merker(NICHT_FRAGEN)) {
      setSchritt('anbieten')
      return
    }
    onFertig()
  }

  async function absenden(e: Event) {
    e.preventDefault()
    setFehler(null)

    if (wiederholungNoetig && geheimnis !== wiederholung) {
      setFehler(t('Die beiden Eingaben stimmen nicht überein.'))
      return
    }
    // Die Regeln gelten beim Anlegen. Beim Entsperren wäre eine Längenprüfung
    // eine Auskunft über das hinterlegte Geheimnis — und im schlimmsten Fall
    // sperrt sie jemanden aus, dessen alter Satz kürzer ist als die heutige Regel.
    if (neu) {
      const schlecht = pruefen(art, geheimnis)
      if (schlecht) { setFehler(schlecht); return }
    }

    setBusy(true)
    try {
      await mitGeheimnis(geheimnis)
      setGeheimnis('')
      setWiederholung('')
      fertigOderAnbieten()
    } catch (e) {
      setFehler(e instanceof Error ? e.message : t('Das hat nicht geklappt.'))
    } finally {
      setBusy(false)
    }
  }

  async function passkeyVersuch() {
    setFehler(null)
    setHinweis(null)
    setBusy(true)
    try {
      if (await mitPasskey()) {
        onFertig()
        return
      }
      // Zeremonie geklappt, aber kein Geheimnis: Das Gerät kann es nicht.
      prfAussichtslosMerken()
      setHinweis(t('Dieses Gerät kann den Passkey dafür nicht nutzen. Bitte tippe es ein.'))
    } catch (e) {
      setFehler(e instanceof Error ? e.message : t('Das hat nicht geklappt.'))
    } finally {
      setBusy(false)
    }
  }

  async function nachruesten() {
    setFehler(null)
    setBusy(true)
    try {
      if (await passkeyNachruesten()) {
        onFertig()
        return
      }
      prfAussichtslosMerken()
      setHinweis(t('Dieses Gerät kann den Passkey dafür nicht nutzen. Es bleibt beim Eintippen.'))
    } catch (e) {
      setFehler(e instanceof Error ? e.message : t('Das hat nicht geklappt.'))
    } finally {
      setBusy(false)
    }
  }

  async function aufschieben() {
    setFehler(null)
    setBusy(true)
    try {
      if (await onAufschieben()) { onFertig(); return }
      setHinweis(t('Das hat der Passkey nicht beantwortet. Bitte tippe es doch ein.'))
    } catch (e) {
      setFehler(e instanceof Error ? e.message : t('Das hat nicht geklappt.'))
    } finally {
      setBusy(false)
    }
  }

  async function vergessen() {
    setFehler(null)
    setBusy(true)
    try {
      await onVergessen()
    } catch (e) {
      setFehler(e instanceof Error ? e.message : t('Das hat nicht geklappt.'))
      setBusy(false)
    }
  }

  if (schritt === 'wiederherstellen') {
    return (
      <div class="fh-anmelden">
        <h1 class="fh-h1">{t('Nachrichten zurückholen')}</h1>
        <p class="fh-muted">
          {t('Wähle das Gerät, dessen Nachrichten du holen willst, und tippe die 12 Wörter von dessen Notfall-Blatt ein.')}
        </p>

        <ul class="fh-liste">
          {sicherungen.map((s) => (
            <li key={s.deviceId}>
              <button
                type="button"
                class={s.deviceId === gewaehlt ? 'fh-zeile' : 'fh-zeile fh-zeile--still'}
                aria-pressed={s.deviceId === gewaehlt}
                onClick={() => setGewaehlt(s.deviceId)}
              >
                <span class="fh-avatar" aria-hidden="true">{s.deviceId === gewaehlt ? '✓' : '·'}</span>
                <span class="fh-zeile__mitte">
                  <span class="fh-zeile__name">{s.deviceName}{s.blocked ? ` (${t('gesperrt')})` : ''}</span>
                  <span class="fh-zeile__unter">
                    {t('Stand {wann}', { wann: new Date(s.updatedAt).toLocaleString() })}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>

        <form
          class="fh-notfallblatt__eingabe"
          onSubmit={(e) => { e.preventDefault(); void holen() }}
        >
          <label class="fh-feld">
            <span>{t('Die 12 Wörter')}</span>
            <textarea
              rows={3}
              autocomplete="off"
              autocapitalize="none"
              spellcheck={false}
              value={woerter}
              onInput={(e) => setWoerter((e.target as HTMLTextAreaElement).value)}
            />
          </label>
          <button
            class="fh-btn fh-btn--gross" type="submit"
            disabled={busy || !gewaehlt || woerter.trim().split(/\s+/).length < 12}
          >
            {busy ? t('Einen Moment…') : t('Nachrichten holen')}
          </button>
        </form>

        <p class="fh-muted">
          {t('Ist das alte Gerät nicht gesperrt, gilt dieses hier für die Familie als dasselbe Gerät. Ist es gesperrt, bleibt dieses neu — der Verlauf kommt trotzdem mit.')}
        </p>

        {fehler && <p class="fh-hinweis fh-hinweis--fehler" role="alert">{fehler}</p>}

        <button
          class="fh-btn fh-btn--quiet" disabled={busy}
          onClick={() => { setFehler(null); setSchritt('entsperren') }}
        >
          {t('Zurück')}
        </button>
      </div>
    )
  }

  if (schritt === 'vergessen') {
    return (
      <div class="fh-anmelden">
        <h1 class="fh-h1">{t('{was} vergessen', { was: artName(art) })}</h1>
        <p class="fh-muted">
          {t('Es gibt kein Zurücksetzen: Was du dir ausgedacht hast, ist nirgends gespeichert, auch nicht auf dem Server. Was bleibt, ist dieses Gerät neu einzurichten.')}
        </p>

        <p class="fh-hinweis fh-hinweis--warnung">
          <span aria-hidden="true">⚠</span>
          <span>
            <strong>{t('Alte Nachrichten sind auf diesem Gerät danach nicht mehr lesbar.')}</strong>{' '}
            {t('Dein Konto, deine Fotos und alles andere bleiben. Auf einem anderen Gerät, das noch entsperrt ist, bleibt der Verlauf ebenfalls erhalten. Deine Familie sieht einen Hinweis, dass du ein neues Gerät benutzt — das ist so gewollt.')}
          </span>
        </p>

        {mitNotfallblatt && (
          <form
            class="fh-notfallblatt__eingabe"
            onSubmit={(e) => { e.preventDefault(); void mitBlatt() }}
          >
            <h2 class="fh-h2">{t('Du hast dein Notfall-Blatt?')}</h2>
            <p class="fh-muted">
              {t('Dann tippe die 12 Wörter ein — in der Reihenfolge vom Blatt. Groß oder klein ist egal.')}
            </p>
            <label class="fh-feld">
              <span>{t('Die 12 Wörter')}</span>
              <textarea
                rows={3}
                autocomplete="off"
                autocapitalize="none"
                spellcheck={false}
                value={woerter}
                onInput={(e) => setWoerter((e.target as HTMLTextAreaElement).value)}
              />
            </label>
            <button class="fh-btn" type="submit" disabled={busy || woerter.trim().split(/\s+/).length < 12}>
              {busy ? t('Einen Moment…') : t('Mit dem Notfall-Blatt öffnen')}
            </button>
          </form>
        )}

        {fehler && <p class="fh-hinweis fh-hinweis--fehler" role="alert">{fehler}</p>}

        <button class="fh-btn fh-btn--gefahr" disabled={busy} onClick={() => void vergessen()}>
          {busy ? t('Einen Moment…') : t('Gerät neu einrichten')}
        </button>
        <button
          class="fh-btn fh-btn--quiet" disabled={busy}
          onClick={() => { setFehler(null); setSchritt('entsperren') }}
        >
          {t('Zurück')}
        </button>
      </div>
    )
  }

  if (schritt === 'anbieten') {
    return (
      <div class="fh-anmelden">
        <h1 class="fh-h1">{t('Nächstes Mal einfacher?')}</h1>
        <p class="fh-muted">
          {t('Du kannst diesen Bildschirm künftig mit deinem Passkey öffnen — Gesicht oder Finger statt tippen. Alle zwei Wochen fragt die App trotzdem einmal nach, damit du es nicht vergisst.')}
        </p>

        {fehler && <p class="fh-hinweis fh-hinweis--fehler" role="alert">{fehler}</p>}
        {hinweis && <p class="fh-hinweis fh-hinweis--info" role="status">{hinweis}</p>}

        <button class="fh-btn fh-btn--gross" disabled={busy} onClick={() => void nachruesten()}>
          <span aria-hidden="true">👆</span> {busy ? t('Einen Moment…') : t('Mit Passkey einrichten')}
        </button>
        <button
          class="fh-btn fh-btn--quiet" disabled={busy}
          onClick={() => { merken(NICHT_FRAGEN); onFertig() }}
        >
          {t('Später')}
        </button>
      </div>
    )
  }

  return (
    <div class="fh-anmelden">
      <h1 class="fh-h1">
        {neu
          ? t('Deine Nachrichten schützen')
          : zweck === 'kontrolle'
            ? t('Kurze Kontrolle')
            : t('Nachrichten entsperren')}
      </h1>

      {zweck === 'kontrolle' && (
        <p class="fh-hinweis fh-hinweis--info">
          <span aria-hidden="true">ℹ️</span>
          <span>
            {t('Alle zwei Wochen fragt die App einmal nach — nicht aus Misstrauen, sondern damit du es nicht vergisst. Wenn dein Telefon einmal weg ist, ist das hier der einzige Weg zurück zu deinen Nachrichten.')}
          </span>
        </p>
      )}

      {passkeyKnopf && (
        <>
          <p class="fh-muted">{t('Leg deinen Finger auf, um deine Nachrichten zu öffnen.')}</p>
          <button
            class="fh-btn fh-btn--gross" disabled={busy} autofocus
            onClick={() => void passkeyVersuch()}
          >
            <span aria-hidden="true">👆</span> {busy ? t('Einen Moment…') : t('Mit Passkey entsperren')}
          </button>
        </>
      )}

      {hinweis && <p class="fh-hinweis fh-hinweis--info" role="status">{hinweis}</p>}

      {/*
        Die Art wird bei der ERSTEN Einrichtung gewählt und später in den
        Einstellungen gewechselt. Hier steht sie, weil sonst jeder mit dem Satz
        anfinge und erst danach erführe, dass es auch eine PIN gibt.
      */}
      {neu && sicherungen.length > 0 && !zurueckgeholt && wiederherstellen && (
        <>
          <p class="fh-hinweis fh-hinweis--info">
            <span aria-hidden="true">📦</span>
            <span>
              <strong>{t('Du hattest schon ein Gerät?')}</strong>{' '}
              {t('Auf eurem Server liegt eine Sicherung deiner Nachrichten. Mit deinem Notfall-Blatt holst du sie auf dieses Gerät.')}
            </span>
          </p>
          <button
            class="fh-btn fh-btn--gross" disabled={busy}
            onClick={() => { setFehler(null); setSchritt('wiederherstellen') }}
          >
            <span aria-hidden="true">📦</span> {t('Nachrichten vom alten Gerät holen')}
          </button>
          <p class="fh-muted">{t('Oder du fängst hier neu an — dann beginnt der Verlauf auf diesem Gerät bei null.')}</p>
        </>
      )}

      {neu && (
        <fieldset class="fh-feld">
          <legend>{t('Womit möchtest du deine Nachrichten schützen?')}</legend>
          <div class="fh-geraet__aktionen">
            {ARTEN.map((a) => (
              <button
                key={a}
                type="button"
                aria-pressed={art === a}
                class={`fh-btn ${art === a ? '' : 'fh-btn--quiet'}`}
                onClick={() => { setFehler(null); onArt(a) }}
              >
                {art === a && <span aria-hidden="true">✓ </span>}{artName(a)}
              </button>
            ))}
          </div>
          <p class="fh-muted">{erklaerung(art)}</p>
          {art === 'pin' && (
            <p class="fh-hinweis fh-hinweis--warnung">
              <span aria-hidden="true">⚠</span>
              <span>
                {t('Eine PIN ist schnell getippt und schnell geraten. Wer dein Telefon in die Hand bekommt, hat es damit am leichtesten. Ein Satz aus vier Wörtern ist deutlich sicherer.')}
              </span>
            </p>
          )}
        </fieldset>
      )}

      {neu ? (
        <p class="fh-muted">
          {t('Das hier schützt deine Nachrichten auf diesem Gerät — auch vor dem Server. Niemand kann es nachschlagen, auch wir nicht.')}
        </p>
      ) : zweck === 'entsperren' ? (
        <p class="fh-muted">
          {passkeyKnopf
            ? t('Oder gib ein, womit du deine Nachrichten geschützt hast.')
            : t('Gib ein, womit du deine Nachrichten geschützt hast.')}
        </p>
      ) : null}

      <form class="fh-card fh-anmelden__formular" onSubmit={(e) => void absenden(e)}>
        <label class="fh-feld">
          <span>{feldName(art, neu)}</span>
          <input
            /*
              `type="text"`, wenn sichtbar — und dann MUSS `autocomplete` aus
              sein. Sonst bietet der Browser an, das Feld mit alten Eingaben zu
              füllen, und schreibt das Geheimnis in seinen eigenen Formularspeicher.
            */
            type={sichtbar ? 'text' : 'password'}
            required
            autofocus={!passkeyKnopf}
            inputMode={tastatur(art)}
            autocomplete={sichtbar ? 'off' : neu ? 'new-password' : 'current-password'}
            autocapitalize="off"
            autocorrect="off"
            spellcheck={false}
            value={geheimnis}
            placeholder={neu ? platzhalter(art) : undefined}
            onInput={(e) => setGeheimnis((e.target as HTMLInputElement).value)}
          />
        </label>

        <button
          type="button"
          class="fh-btn fh-btn--link"
          // Nicht nur das Auge: Das Symbol allein sagt nicht, in welche Richtung
          // es schaltet — und Farbe oder Bild allein trägt hier nie (Anf. 58).
          aria-pressed={sichtbar}
          onClick={() => setSichtbar((s) => !s)}
        >
          <span aria-hidden="true">{sichtbar ? '🙈' : '👁'}</span>{' '}
          {sichtbar ? t('Verbergen') : t('Anzeigen')}
        </button>

        {wiederholungNoetig && (
          <label class="fh-feld">
            <span>{t('Noch einmal')}</span>
            <input
              type="password" required autocomplete="new-password"
              inputMode={tastatur(art)} value={wiederholung}
              onInput={(e) => setWiederholung((e.target as HTMLInputElement).value)}
            />
          </label>
        )}

        {fehler && <p class="fh-hinweis fh-hinweis--fehler" role="alert">{fehler}</p>}

        <button class="fh-btn" type="submit" disabled={busy}>
          {busy
            ? t('Einen Moment…')
            : neu
              ? t('Weiter')
              : zweck === 'kontrolle'
                ? t('Stimmt')
                : t('Entsperren')}
        </button>
      </form>

      {neu ? (
        <p class="fh-hinweis fh-hinweis--info">
          <span aria-hidden="true">ℹ️</span>
          <span>
            {zurueckgeholt
              ? t('Dein Notfall-Blatt vom alten Gerät gilt weiter — auch für die Sicherung dieses Geräts.')
              : t('Wenn du das vergisst und alle deine Geräte verlierst, sind deine alten Nachrichten weg — auch wir kommen dann nicht mehr daran. Im nächsten Schritt bekommst du dafür ein Notfall-Blatt.')}
          </span>
        </p>
      ) : zweck === 'kontrolle' ? (
        <>
          {/*
            Ohne diesen Ausweg wäre die Kontrolle eine Falle: Wer am vierzehnten
            Tag gerade nicht darauf kommt, käme an seine Nachrichten nicht mehr
            heran — durch die Vorkehrung, die genau das verhindern soll.
          */}
          <button
            class="fh-btn fh-btn--quiet" disabled={busy}
            onClick={() => void aufschieben()}
          >
            {t('Ich komme gerade nicht darauf')}
          </button>
          <p class="fh-muted">
            {t('Dann geht es heute mit dem Passkey weiter und die App fragt in drei Tagen wieder. Sieh bis dahin auf deinem Notfall-Blatt nach.')}
          </p>
        </>
      ) : (
        <button
          class="fh-btn fh-btn--link fh-anmelden__hilfe"
          onClick={() => setSchritt('vergessen')}
        >
          {t('{was} vergessen?', { was: artName(art) })}
        </button>
      )}
    </div>
  )
}
